/**
 * Frontend Logger - loglevel wrapper for Vercel debugging
 *
 * Usage:
 *   import log from '@/lib/logger';
 *   log.debug('Debug message', { data });
 *   log.info('Info message');
 *   log.warn('Warning');
 *   log.error('Error', error);
 *
 * Set VITE_LOG_LEVEL in .env to control output: debug | info | warn | error
 * In production (Vercel), logs appear in Runtime Logs
 */
import log from 'loglevel';
import { captureLog } from './logTransport';

// Get log level from env, default to 'debug' in dev, 'warn' in prod
const envLevel = import.meta.env.VITE_LOG_LEVEL;
const defaultLevel = import.meta.env.PROD ? 'warn' : 'debug';
const level = (envLevel || defaultLevel) as log.LogLevelDesc;

log.setLevel(level);

// Add timestamp prefix to all logs for Vercel debugging
const originalFactory = log.methodFactory;
log.methodFactory = function (methodName, logLevel, loggerName) {
  const rawMethod = originalFactory(methodName, logLevel, loggerName);

  return function (...args: unknown[]) {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}]${loggerName ? ` [${loggerName}]` : ''}`;
    rawMethod(prefix, ...args);
  };
};

// Apply the custom factory
log.setLevel(log.getLevel());

// Named loggers for different modules
export const createLogger = (name: string) => {
  const namedLogger = log.getLogger(name);
  namedLogger.setLevel(level);

  // Apply same factory to named loggers
  const namedFactory = namedLogger.methodFactory;
  namedLogger.methodFactory = function (methodName, logLevel, loggerName) {
    const rawMethod = namedFactory(methodName, logLevel, loggerName);
    return function (...args: unknown[]) {
      const timestamp = new Date().toISOString();
      rawMethod(`[${timestamp}] [${loggerName}]`, ...args);
      const message = args
        .map((a) => {
          if (typeof a === 'string') return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(' ');
      captureLog(methodName, (loggerName as string) || null, message);
    };
  };
  namedLogger.setLevel(namedLogger.getLevel());

  return namedLogger;
};

// Pre-configured loggers for common modules
export const apiLogger = createLogger('API');
export const sseLogger = createLogger('SSE');
export const authLogger = createLogger('AUTH');
export const uiLogger = createLogger('UI');
export const perfLogger = createLogger('PERF');
export const parseLogger = createLogger('PARSER');

export default log;
